import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, Trash, PencilSimple, Check } from "@phosphor-icons/react";

export function RegionsPanel({ settings, setSettings, isAdmin, onRenameRegion, onRenameLocation }) {
  const regions = settings.regions || [];
  const [newRegion, setNewRegion] = useState("");
  const [locDraft, setLocDraft] = useState({});
  const [editingRegionIdx, setEditingRegionIdx] = useState(null);
  const [regionDraft, setRegionDraft] = useState("");
  const [editingLoc, setEditingLoc] = useState(null); // { regionIdx, loc }
  const [locEditDraft, setLocEditDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const addRegion = () => {
    const name = newRegion.trim();
    if (!name) return;
    if (regions.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
      setNewRegion("");
      return;
    }
    setSettings((s) => ({ ...s, regions: [...(s.regions || []), { name, locations: [] }] }));
    setNewRegion("");
  };

  const removeRegion = (idx) => {
    setSettings((s) => ({ ...s, regions: (s.regions || []).filter((_, i) => i !== idx) }));
  };

  const addLocation = (idx) => {
    const v = (locDraft[idx] || "").trim();
    if (!v) return;
    setSettings((s) => {
      const next = [...(s.regions || [])];
      const cur = next[idx] || { name: "", locations: [] };
      if (cur.locations.some((x) => x.toLowerCase() === v.toLowerCase())) return s;
      next[idx] = { ...cur, locations: [...cur.locations, v] };
      return { ...s, regions: next };
    });
    setLocDraft((d) => ({ ...d, [idx]: "" }));
  };

  const removeLocation = (idx, loc) => {
    setSettings((s) => {
      const next = [...(s.regions || [])];
      const cur = next[idx];
      if (!cur) return s;
      next[idx] = { ...cur, locations: cur.locations.filter((x) => x !== loc) };
      return { ...s, regions: next };
    });
  };

  const startEditRegion = (idx, name) => { setEditingRegionIdx(idx); setRegionDraft(name); };
  const cancelEditRegion = () => { setEditingRegionIdx(null); setRegionDraft(""); };
  const saveEditRegion = async () => {
    if (editingRegionIdx === null) return;
    const from = regions[editingRegionIdx]?.name;
    const to = regionDraft.trim();
    if (!from || !to || from === to) { cancelEditRegion(); return; }
    if (!onRenameRegion) {
      // fallback local
      setSettings((s) => {
        const next = [...(s.regions || [])];
        next[editingRegionIdx] = { ...next[editingRegionIdx], name: to };
        return { ...s, regions: next };
      });
      cancelEditRegion();
      return;
    }
    setSaving(true);
    try {
      await onRenameRegion(from, to);
      cancelEditRegion();
    } finally { setSaving(false); }
  };

  const startEditLoc = (regionIdx, loc) => { setEditingLoc({ regionIdx, loc }); setLocEditDraft(loc); };
  const cancelEditLoc = () => { setEditingLoc(null); setLocEditDraft(""); };
  const saveEditLoc = async () => {
    if (!editingLoc) return;
    const { regionIdx, loc: from } = editingLoc;
    const to = locEditDraft.trim();
    const regionName = regions[regionIdx]?.name;
    if (!regionName || !from || !to || from === to) { cancelEditLoc(); return; }
    if (!onRenameLocation) {
      setSettings((s) => {
        const next = [...(s.regions || [])];
        const cur = next[regionIdx];
        if (cur) next[regionIdx] = { ...cur, locations: cur.locations.map((x) => (x === from ? to : x)) };
        return { ...s, regions: next };
      });
      cancelEditLoc();
      return;
    }
    setSaving(true);
    try {
      await onRenameLocation(regionName, from, to);
      cancelEditLoc();
    } finally { setSaving(false); }
  };

  return (
    <div className="hairline rounded-lg p-5 bg-white" data-testid="settings-regions">
      <div className="font-heading font-semibold">Regions &amp; locations</div>
      <p className="text-xs text-slate-500 mt-1">
        Two-layer geography taxonomy. Add regions (e.g. UK, Australia, US) and the sites under each region (e.g. Dyce, Huntly). Use the pencil to rename any value — renames cascade to every ticket that uses it. AI will use these to tag incoming tickets and flag anything it can&apos;t map.
      </p>

      <div className="mt-4 space-y-4">
        {regions.length === 0 && (
          <div className="text-xs text-slate-500 italic">No regions configured yet — add your first region below.</div>
        )}
        {regions.map((r, idx) => {
          const editingThisRegion = editingRegionIdx === idx;
          return (
            <div key={`${r.name}-${idx}`} className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid={`region-row-${idx}`}>
              <div className="flex items-center gap-2">
                {editingThisRegion ? (
                  <span className="inline-flex items-center gap-1 bg-white border border-[#4f4c90]/40 rounded-md pl-2 pr-1 py-0.5">
                    <Input
                      autoFocus
                      value={regionDraft}
                      onChange={(e) => setRegionDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); saveEditRegion(); }
                        if (e.key === "Escape") { e.preventDefault(); cancelEditRegion(); }
                      }}
                      disabled={saving}
                      className="h-6 text-xs w-40 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-1"
                      data-testid={`region-${idx}-rename-input`}
                    />
                    <button onClick={saveEditRegion} disabled={saving} className="p-0.5 text-[#39A36E] hover:text-[#2E8C5D]" data-testid={`region-${idx}-rename-save`}><Check size={12} weight="bold" /></button>
                    <button onClick={cancelEditRegion} disabled={saving} className="p-0.5 text-slate-400 hover:text-red-500" data-testid={`region-${idx}-rename-cancel`}><X size={12} weight="bold" /></button>
                  </span>
                ) : (
                  <>
                    <span className="chip !text-[#8B87C8] !border-[#4f4c90]/30 bg-[#4f4c90]/10 gap-1 pl-2 pr-2 py-0.5 text-xs font-medium">
                      {r.name}
                    </span>
                    {isAdmin && (
                      <button
                        onClick={() => startEditRegion(idx, r.name)}
                        className="text-slate-400 hover:text-[#4f4c90] p-0.5"
                        data-testid={`region-${idx}-edit`}
                        title="Rename region"
                      >
                        <PencilSimple size={12} weight="bold" />
                      </button>
                    )}
                  </>
                )}
                <span className="text-[11px] text-slate-500">{r.locations?.length || 0} location(s)</span>
                {isAdmin && !editingThisRegion && (
                  <button
                    onClick={() => removeRegion(idx)}
                    className="ml-auto text-slate-400 hover:text-red-500 p-1"
                    data-testid={`region-remove-${idx}`}
                    aria-label={`Remove ${r.name}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 pl-1 items-center">
                {(r.locations || []).map((loc) => {
                  const isEditing = editingLoc && editingLoc.regionIdx === idx && editingLoc.loc === loc;
                  if (isEditing) {
                    return (
                      <span key={loc} className="inline-flex items-center gap-1 bg-white border border-[#4f4c90]/40 rounded-md pl-2 pr-1 py-0.5" data-testid={`region-${idx}-loc-editing-${loc}`}>
                        <Input
                          autoFocus
                          value={locEditDraft}
                          onChange={(e) => setLocEditDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); saveEditLoc(); }
                            if (e.key === "Escape") { e.preventDefault(); cancelEditLoc(); }
                          }}
                          disabled={saving}
                          className="h-6 text-xs w-36 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-1"
                          data-testid={`region-${idx}-loc-rename-input`}
                        />
                        <button onClick={saveEditLoc} disabled={saving} className="p-0.5 text-[#39A36E] hover:text-[#2E8C5D]" data-testid={`region-${idx}-loc-rename-save`}><Check size={11} weight="bold" /></button>
                        <button onClick={cancelEditLoc} disabled={saving} className="p-0.5 text-slate-400 hover:text-red-500" data-testid={`region-${idx}-loc-rename-cancel`}><X size={11} weight="bold" /></button>
                      </span>
                    );
                  }
                  return (
                    <span key={loc} className="chip !text-slate-800 !border-slate-300 bg-slate-100 gap-1 pl-2 pr-1 py-0.5">
                      {loc}
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => startEditLoc(idx, loc)}
                            className="hover:text-[#4f4c90] p-0.5"
                            data-testid={`region-${idx}-loc-edit-${loc}`}
                            title="Rename location"
                          >
                            <PencilSimple size={10} weight="bold" />
                          </button>
                          <button
                            onClick={() => removeLocation(idx, loc)}
                            className="hover:text-red-400 p-0.5"
                            data-testid={`region-${idx}-loc-remove-${loc}`}
                            aria-label={`Remove ${loc}`}
                          >
                            <X size={10} weight="bold" />
                          </button>
                        </>
                      )}
                    </span>
                  );
                })}
                {(r.locations || []).length === 0 && <span className="text-xs text-slate-500 italic">No locations under {r.name} yet.</span>}
              </div>
              {isAdmin && (
                <div className="mt-2 flex gap-2 pl-1">
                  <Input
                    value={locDraft[idx] || ""}
                    onChange={(e) => setLocDraft((d) => ({ ...d, [idx]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(idx); } }}
                    placeholder={`Add location under ${r.name}`}
                    className="bg-white border-slate-200 h-8 text-sm max-w-xs"
                    data-testid={`region-${idx}-loc-input`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-200 bg-slate-100 h-8 gap-1"
                    onClick={() => addLocation(idx)}
                    data-testid={`region-${idx}-loc-add`}
                  >
                    <Plus size={12} /> Add
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div className="mt-4 flex gap-2 pt-3 border-t border-slate-200">
          <Input
            value={newRegion}
            onChange={(e) => setNewRegion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRegion(); } }}
            placeholder="New region name (e.g. UK, Australia, US)"
            className="bg-white border-slate-200 h-9 text-sm max-w-xs"
            data-testid="region-add-input"
          />
          <Button
            size="sm"
            className="bg-[#39A36E] hover:bg-[#2E8C5D] h-9 gap-1"
            onClick={addRegion}
            data-testid="region-add-btn"
          >
            <Plus size={12} /> Add region
          </Button>
        </div>
      )}
    </div>
  );
}
